/**
 * [Phase 2b-D §6.5.2 R5] Nomination Order Panel for DraftLobby.
 *
 * Drag-and-drop ordering of teams. Renders only when:
 *   - Commissioner is viewing
 *   - league.status in (SETUP, LOBBY)
 *   - league.settings.nomination_order_mode === 'preset'
 *   - All seats filled (humans + CPUs === team_count) - handled by parent
 *
 * On Save: PUT /api/leagues/{id}/nomination-order (built in 2b-A).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Save, Shuffle, ListOrdered } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";
import ManagerName from "../ManagerName";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableRow = ({ index, manager }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: manager.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`lobby-nomorder-row-${manager.id}`}
      className="flex items-center gap-3 bg-[#1C1C1C] border border-[#252525] rounded-lg px-3 py-2"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`lobby-nomorder-handle-${manager.id}`}
        className="cursor-grab active:cursor-grabbing text-[#A0A0A0] hover:text-[#FF6B00] touch-none"
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>
      <span className="font-display font-black text-base text-[#FF6B00] w-6 text-center">
        {index + 1}
      </span>
      <span className="flex-1 font-body text-sm text-[#F5F5F5]">
        <ManagerName manager={manager} />
      </span>
    </li>
  );
};

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function NominationOrderPanel({ league, managers }) {
  // Initialize order from league.settings.nomination_order_pending if present.
  const [order, setOrder] = useState(() => {
    const pending = league?.settings?.nomination_order_pending;
    if (Array.isArray(pending) && pending.length === managers.length) {
      // Filter to only managers we know about and append any new ones.
      const known = pending.filter((id) => managers.some((m) => m.id === id));
      const missing = managers.filter((m) => !known.includes(m.id)).map((m) => m.id);
      return [...known, ...missing];
    }
    return managers.map((m) => m.id);
  });
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");

  // Re-sync if the manager list grows (e.g., new seat seated).
  useEffect(() => {
    setOrder((prev) => {
      const known = prev.filter((id) => managers.some((m) => m.id === id));
      const missing = managers.filter((m) => !known.includes(m.id)).map((m) => m.id);
      return [...known, ...missing];
    });
  }, [managers]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const onRandomize = () => {
    setOrder((items) => shuffleArray(items));
  };

  const onSave = async () => {
    setError("");
    setBusy(true);
    try {
      await axios.put(
        `${API}/leagues/${league.id}/nomination-order`,
        { order },
        { headers: authHeaders() },
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save order");
    } finally {
      setBusy(false);
    }
  };

  const orderedManagers = order
    .map((id) => managers.find((m) => m.id === id))
    .filter(Boolean);

  return (
    <div
      data-testid="lobby-nomorder-panel"
      className="glass-card rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 text-[#F5F5F5]">
          <ListOrdered size={16} className="text-[#FF6B00]" /> Nomination Order
        </h2>
        {savedFlash && (
          <span className="text-[#00C853] font-body text-xs">Saved</span>
        )}
      </div>

      {error && (
        <div className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-3 py-2 text-[#FF4444] font-body text-xs mb-3">
          {error}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ol className="space-y-2 mb-4">
            {orderedManagers.map((m, idx) => (
              <SortableRow key={m.id} index={idx} manager={m} />
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRandomize}
          data-testid="lobby-nomorder-randomize-btn"
          className="inline-flex items-center gap-1 bg-[#252525] hover:bg-[#333] text-[#F5F5F5] font-display font-bold text-xs uppercase px-3 py-2 rounded-lg transition-colors"
        >
          <Shuffle size={12} /> Randomize
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          data-testid="lobby-nomorder-save-btn"
          className="ml-auto inline-flex items-center gap-1 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 text-white font-display font-bold text-xs uppercase px-3 py-2 rounded-lg transition-colors"
        >
          <Save size={12} /> Save Order
        </button>
      </div>
    </div>
  );
}
